import type { Extension } from "@codemirror/state"
import type { JSONSchema7 } from "json-schema"
import { Suspense, lazy, useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  Field,
  FieldDescriptionOrError,
  IconButton,
  cn,
  useTheme,
} from "@motleyhand/design"
import { ArrowDiagonalIcon } from "@hugeicons/core-free-icons"
import { getExtensions, getTheme } from "./getExtensions"
import type { ScriptLanguage } from "./model"

const CodeMirror = lazy(() => import("@uiw/react-codemirror"))

type Props = {
  className?: string
  language: ScriptLanguage | "json"
  code: string
  onChange: (code: string) => void
  helperText?: string
  error?: string
  readOnly?: boolean
  allowFullScreen?: boolean
  fillHeight?: boolean
  jsonSchema?: JSONSchema7
}

export default function CodeEditor({
  className,
  language,
  code,
  onChange,
  helperText,
  error,
  readOnly,
  allowFullScreen = true,
  fillHeight = false,
  jsonSchema,
}: Props) {
  const { colorScheme } = useTheme()
  const [extensions, setExtensions] = useState<Extension[]>([])
  const [codeTheme, setCodeTheme] = useState<Extension>()
  const [isFullScreen, setFullScreen] = useState(false)

  useEffect(() => {
    void (async () => {
      setExtensions(await getExtensions(language, jsonSchema))
    })()
  }, [language, jsonSchema])

  useEffect(() => {
    void (async () => {
      setCodeTheme(await getTheme(colorScheme))
    })()
  }, [colorScheme])

  const editor = (
    <Suspense>
      <CodeMirror
        key={language}
        className={cn(
          "nodrag nowheel overflow-auto",
          isFullScreen
            ? "h-full max-h-full w-full max-w-full"
            : fillHeight
              ? `min-h-0 flex-1 cursor-text border ${error ? "border-destructive" : "border-muted-foreground"}`
              : `max-h-120 min-h-40 cursor-text border ${error ? "border-destructive" : "border-muted-foreground"}`,
        )}
        value={code}
        theme={codeTheme}
        extensions={extensions}
        onChange={onChange}
        readOnly={readOnly}
      />
    </Suspense>
  )

  return (
    <Field
      className={cn(
        "w-full",
        fillHeight ? "flex min-h-0 flex-1 flex-col" : "min-h-50",
        className,
      )}
      aria-invalid={!!error}
    >
      {isFullScreen ? (
        <Dialog open onOpenChange={setFullScreen}>
          <DialogContent
            spark="purple"
            className="flex max-h-4/5 min-h-48 w-full flex-col p-4 sm:w-5/6 sm:max-w-full"
          >
            <div className="min-h-0 flex-1 overflow-auto">{editor}</div>
          </DialogContent>
        </Dialog>
      ) : (
        <div
          className={cn(
            "relative",
            fillHeight && "flex min-h-0 flex-1 flex-col",
          )}
        >
          {editor}
          {allowFullScreen && (
            <IconButton
              icon={ArrowDiagonalIcon}
              size="icon-xs"
              className="absolute right-1 bottom-1"
              onClick={() => {
                setFullScreen(true)
              }}
            />
          )}
        </div>
      )}
      <FieldDescriptionOrError description={helperText} error={error} />
    </Field>
  )
}
